import React, { createContext, useContext, useState, useEffect } from 'react';
import api, { clearAuthToken } from '../utils/api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    clearAuthToken();
    fetchCurrentUser();
  }, []);

  const fetchCurrentUser = async () => {
    try {
      const response = await api.get('/auth/me');
      setUser(response.data);
    } catch (error) {
      setUser(null);
      clearAuthToken();
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const response = await api.post('/auth/login', { email, password });
    const { user: userData } = response.data;
    setUser(userData);
    return userData;
  };

  const signup = async (name, email, password, referralCode = null, marketingOptIn = false) => {
    const response = await api.post('/auth/signup', { 
      name, 
      email, 
      password,
      referral_code: referralCode,
      marketing_opt_in: marketingOptIn
    });
    const { user: userData } = response.data;
    setUser(userData);
    return userData;
  };

  const clearSession = () => {
    clearAuthToken();
    setUser(null);
  };

  const logout = async () => {
    // Do not claim sign-out if server revocation failed.
    await api.post('/auth/logout');
    clearSession();
    window.location.href = '/';
  };

  const confirmEmailLink = async (payload) => {
    const { data } = await api.post('/auth/magic-link/confirm', payload);
    setUser(data.user);
    return data;
  };

  const refreshUser = async () => {
    try {
      const response = await api.get('/auth/me');
      setUser(response.data);
      return response.data;
    } catch (error) {
      console.error('Failed to refresh user:', error);
      return null;
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        login,
        signup,
        confirmEmailLink,
        logout,
        clearSession,
        refreshUser,
        isAuthenticated: !!user,
        isAdmin: user?.role === 'admin',
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
